import { useState } from 'react';

interface BulkPlanChangeModalProps {
  affectedSlugs: string[];
  onConfirm: (plan: 'basic' | 'business' | 'enterprise', reason: string) => void;
  onCancel: () => void;
}

/**
 * Bulk plan-change dialog. Requires both a target plan and a
 * non-empty reason — reason is mirrored into each per-tenant
 * /plan request's body, so the existing PlanHistory row carries
 * the operator's justification just like a single-tenant change.
 */
export function BulkPlanChangeModal({ affectedSlugs, onConfirm, onCancel }: BulkPlanChangeModalProps) {
  const [plan, setPlan] = useState<'basic' | 'business' | 'enterprise'>('business');
  const [reason, setReason] = useState('');

  // Trim before validating so a whitespace-only reason still
  // blocks the button — operators occasionally hit space + enter
  // by accident, and a blank reason would render as "—" in the
  // plan-history table.
  const reasonOk = reason.trim().length > 0;

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded shadow-lg w-full max-w-lg">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold">Plan ändern (bulk)</h3>
          <p className="text-sm text-slate-600 mt-1">
            Plan-Wechsel für {affectedSlugs.length} Tenant(s). Begründung wird
            in der Plan-Historie pro Tenant gespeichert.
          </p>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <p className="text-xs text-slate-500 mb-1">Betroffene Tenants ({affectedSlugs.length}):</p>
            <div className="max-h-32 overflow-y-auto bg-slate-50 rounded p-2 text-xs font-mono">
              {affectedSlugs.join(', ')}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Ziel-Plan</label>
            <select
              value={plan}
              onChange={(e) => setPlan(e.target.value as 'basic' | 'business' | 'enterprise')}
              className="w-full px-2 py-1 rounded border"
            >
              <option value="basic">basic</option>
              <option value="business">business</option>
              <option value="enterprise">enterprise</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              Begründung <span className="text-red-600">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="z.B. Migration Welle 4.6 — Tier-Konsolidierung Region EU-South"
              rows={3}
              className="w-full px-2 py-1 rounded border text-sm"
              required
            />
            <p className="text-xs text-slate-500 mt-1">
              Pflicht — wird je Tenant in die Plan-Historie geschrieben.
            </p>
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <button onClick={onCancel} className="px-3 py-1 rounded border">
            Abbrechen
          </button>
          <button
            onClick={() => onConfirm(plan, reason.trim())}
            disabled={!reasonOk}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Plan ändern
          </button>
        </div>
      </div>
    </div>
  );
}
